import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentPage = () => {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/pages/${slug}`);
        setPage(response.data);
      } catch (err) {
        setError(t('content.notFound'));
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const getTitle = () => {
    if (!page) return '';
    if (i18n.language === 'en' && page.title_en) return page.title_en;
    if (i18n.language === 'pt' && page.title_pt) return page.title_pt;
    return page.title;
  };

  const getContent = () => {
    if (!page) return '';
    if (i18n.language === 'en' && page.content_en) return page.content_en;
    if (i18n.language === 'pt' && page.content_pt) return page.content_pt;
    return page.content;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-8 w-32 mb-6 bg-[#141414]" />
          <Skeleton className="h-12 w-3/4 mb-8 bg-[#141414]" />
          <Skeleton className="h-96 w-full bg-[#141414]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-zinc-500">{error}</p>
          <Button
            variant="outline"
            className="mt-4 border-white/20 text-zinc-400 hover:text-white"
            onClick={() => navigate('/')}
          >
            {t('common.back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>

        <h1 className="font-heading text-3xl sm:text-4xl text-white mb-8" data-testid="page-title">
          {getTitle()}
        </h1>

        <Card className="card-presage rounded-sm">
          <CardContent className="p-8">
            <div className="prose prose-invert prose-zinc max-w-none" data-testid="page-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-2xl font-heading text-white mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-semibold text-white mt-8 mb-4">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-6 mb-3">{children}</h3>,
                  p: ({ children }) => <p className="text-zinc-400 mb-4 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside text-zinc-400 mb-4 space-y-2">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside text-zinc-400 mb-4 space-y-2">{children}</ol>,
                  li: ({ children }) => <li className="text-zinc-400">{children}</li>,
                  strong: ({ children }) => <strong className="text-white font-semibold">{children}</strong>,
                  a: ({ href, children }) => (
                    <a href={href} className="text-white underline hover:text-zinc-300" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  hr: () => <hr className="border-white/10 my-8" />,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-white/20 pl-4 my-4 text-zinc-500 italic">
                      {children}
                    </blockquote>
                  ),
                }}
              >
                {getContent()}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>

        {page?.updated_at && (
          <p className="text-zinc-600 text-sm mt-4 text-right">
            {t('content.lastUpdated', { date: new Date(page.updated_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'pt' ? 'pt-BR' : 'ja-JP') })}
          </p>
        )}
      </div>
    </div>
  );
};

export default ContentPage;
